import React, { useState } from 'react';
import { Zap, Calendar, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';

interface OptimizationResult {
  originalEndDate: string;
  optimizedEndDate: string;
  daysSaved: number;
  tasksReordered: number;
  criticalPathChanges: number;
  riskReduction: number;
}

interface PlanOptimizerProps {
  projectId: string;
  userRole: 'client' | 'vendor' | 'admin';
}

export const PlanOptimizer: React.FC<PlanOptimizerProps> = ({ 
  projectId, 
  userRole 
}) => {
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationResult, setOptimizationResult] = useState<OptimizationResult | null>(null);
  const [optimizationSettings, setOptimizationSettings] = useState({
    prioritizeSpeed: true,
    maintainQuality: true,
    respectDependencies: true,
    considerResourceConstraints: true
  });

  const runOptimization = async () => {
    setIsOptimizing(true);
    
    setTimeout(() => {
      const result: OptimizationResult = {
        originalEndDate: '2026-09-30',
        optimizedEndDate: '2026-09-22',
        daysSaved: 8,
        tasksReordered: 12,
        criticalPathChanges: 3,
        riskReduction: 15
      };
      
      setOptimizationResult(result);
      setIsOptimizing(false);
    }, 3000);
  };

  const applyOptimization = () => {
    console.log('Applying optimization to project timeline...');
    setOptimizationResult(null);
  };

  return (
    <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
      <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
        <Zap className="w-5 h-5 text-[#4267A1] mr-2" />
        Plan Optimizer
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h4 className="font-medium text-gray-900 mb-4">Optimization Settings</h4>
          
          <div className="space-y-4">
            {[
              { key: 'prioritizeSpeed', label: 'Prioritize Speed', description: 'Optimize for fastest completion' },
              { key: 'maintainQuality', label: 'Maintain Quality', description: 'Preserve quality gates and reviews' },
              { key: 'respectDependencies', label: 'Respect Dependencies', description: 'Honor task dependencies and prerequisites' },
              { key: 'considerResourceConstraints', label: 'Resource Constraints', description: 'Account for team capacity and availability' }
            ].map(({ key, label, description }) => (
              <div key={key} className="flex items-start space-x-3">
                <button
                  onClick={() => setOptimizationSettings(prev => ({ 
                    ...prev, 
                    [key]: !prev[key as keyof typeof prev] 
                  }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    optimizationSettings[key as keyof typeof optimizationSettings] ? 'bg-[#8A9150]' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      optimizationSettings[key as keyof typeof optimizationSettings] ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <div>
                  <h5 className="font-medium text-gray-900">{label}</h5>
                  <p className="text-sm text-gray-600">{description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6">
            <button
              onClick={runOptimization}
              disabled={isOptimizing || userRole === 'client'}
              className="w-full px-4 py-3 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isOptimizing ? 'Optimizing Plan...' : 'Optimize Plan'}
            </button>
            
            {userRole === 'client' && (
              <p className="text-sm text-gray-500 mt-2">
                Plan optimization is available to project managers only
              </p>
            )}
          </div>
        </div>

        <div>
          {isOptimizing && (
            <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-3 mb-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#4267A1]"></div>
                <h4 className="font-medium text-blue-900">Analyzing Project Plan</h4>
              </div>
              <div className="space-y-2 text-sm text-blue-800">
                <p>• Evaluating task dependencies and critical path</p>
                <p>• Analyzing resource capacity and constraints</p>
                <p>• Identifying optimization opportunities</p>
                <p>• Calculating timeline improvements</p>
              </div>
            </div>
          )}

          {optimizationResult && (
            <div className="space-y-4">
              <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-3 mb-4">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <h4 className="font-medium text-green-900">Optimization Complete</h4>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-green-700">Original End Date</p>
                    <p className="font-semibold text-green-900">
                      {new Date(optimizationResult.originalEndDate).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-green-700">Optimized End Date</p>
                    <p className="font-semibold text-green-900">
                      {new Date(optimizationResult.optimizedEndDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <span className="text-lg font-bold text-green-900">
                    {optimizationResult.daysSaved} days saved
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium text-gray-900 mb-1">Tasks Reordered</h5>
                  <p className="text-2xl font-bold text-[#4267A1]">{optimizationResult.tasksReordered}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium text-gray-900 mb-1">Critical Path Changes</h5>
                  <p className="text-2xl font-bold text-[#8A9150]">{optimizationResult.criticalPathChanges}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h5 className="font-medium text-gray-900 mb-1">Risk Reduction</h5>
                  <p className="text-2xl font-bold text-purple-600">{optimizationResult.riskReduction}%</p>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={applyOptimization}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-[#8A9150] to-[#A5B366] text-white rounded-lg hover:shadow-lg hover:shadow-[#8A9150]/25 transition-all duration-200 font-medium"
                >
                  Apply Optimization
                </button>
                <button
                  onClick={() => setOptimizationResult(null)}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!isOptimizing && !optimizationResult && (
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3 mb-4">
                <Calendar className="w-6 h-6 text-gray-600" />
                <h4 className="font-medium text-gray-900">Current Plan Status</h4>
              </div>
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex justify-between">
                  <span>Project End Date:</span>
                  <span className="font-medium">September 30, 2026</span>
                </div>
                <div className="flex justify-between">
                  <span>Critical Path Tasks:</span>
                  <span className="font-medium">8 tasks</span>
                </div>
                <div className="flex justify-between">
                  <span>Resource Utilization:</span>
                  <span className="font-medium text-yellow-600">87% (High)</span>
                </div>
                <div className="flex justify-between">
                  <span>Risk Level:</span>
                  <span className="font-medium text-red-600">Medium</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 text-xs text-gray-500">
        <p>• Plan optimization uses OnboardingAgenticService for intelligent scheduling</p>
        <p>• All optimizations preserve quality gates and client requirements</p>
        <p>• Changes can be reviewed and approved before implementation</p>
      </div>
    </div>
  );
};
